<script setup lang="ts">
  import Popup from '@/components/Popup.vue'
  import Sheet from '@/components/Sheet.vue'
  import { useOpenAlert } from '@/composables/use-open-alert'
  import { useAlertStore } from '@/store/alert.store'
  import { AlertStatus, type Alert } from '@/types/nest/alert'
  import { ChevronDown, UiButton, useToastStore } from '@logistiek-zonder-papier/lzp-library'
  import {
    format,
    formatDistanceToNowStrict,
    isSameYear,
    isToday,
    isYesterday,
    startOfToday,
    subMonths,
    subWeeks,
  } from 'date-fns'
  import { nl } from 'date-fns/locale'
  import { useTranslation } from 'i18next-vue'
  import { computed, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'

  type Tab = 'all' | 'unread'
  type Period = 'week' | 'month' | 'all'
  type AlertGroup = { key: string; label: string; alerts: Alert[] }

  const periodLabels: Record<Period, string> = {
    week: 'Afgelopen week',
    month: 'Afgelopen maand',
    all: 'Alles',
  }

  const alertStore = useAlertStore()
  const toastStore = useToastStore()
  const router = useRouter()
  const { i18next } = useTranslation()
  const { openAlert } = useOpenAlert()

  const show = defineModel<boolean>('show', { required: true })

  const tab = ref<Tab>('unread')
  const period = ref<Period>('week')

  const unreadAlerts = computed(() =>
    alertStore.recentAlerts.filter(alert => alert.status === AlertStatus.UNREAD)
  )

  const alertGroups = computed<AlertGroup[]>(() => {
    const alerts = tab.value === 'unread' ? unreadAlerts.value : alertStore.recentAlerts
    const groups: AlertGroup[] = []

    for (const alert of alerts) {
      const createdAt = new Date(alert.createdAt)
      const key = format(createdAt, 'yyyy-MM-dd')
      const lastGroup = groups.at(-1)

      if (lastGroup?.key === key) {
        lastGroup.alerts.push(alert)
      } else {
        groups.push({ key, label: formatGroupLabel(createdAt), alerts: [alert] })
      }
    }

    return groups
  })

  const emptyMessage = computed(() =>
    tab.value === 'unread' ? 'Geen ongelezen meldingen' : 'Geen meldingen in deze periode'
  )

  watch([show, period], async () => {
    if (!show.value) return
    await alertStore.fetchRecentAlerts(getFromDate(period.value))
  })

  function getFromDate(period: Period) {
    switch (period) {
      case 'week':
        return subWeeks(startOfToday(), 1)
      case 'month':
        return subMonths(startOfToday(), 1)
      case 'all':
        return null
    }
  }

  function formatGroupLabel(date: Date) {
    if (isToday(date)) return 'Vandaag'
    if (isYesterday(date)) return 'Gisteren'
    return format(date, isSameYear(date, new Date()) ? 'd MMMM' : 'd MMMM yyyy', { locale: nl })
  }

  function formatDateTime(date: string) {
    const createdAt = new Date(date)
    const dateTime = new Intl.DateTimeFormat(i18next.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(createdAt)

    if (isToday(createdAt)) {
      return `${formatDistanceToNowStrict(createdAt, { addSuffix: true, locale: nl })} · ${dateTime}`
    }
    return dateTime
  }

  function selectPeriod(selectedPeriod: Period, close: () => void) {
    period.value = selectedPeriod
    close()
  }

  async function markAsRead(alert: Alert) {
    if (alert.status !== AlertStatus.UNREAD) return
    await alertStore.updateAlertStatus(alert.id, AlertStatus.READ)
    toastStore.success('Melding gemarkeerd als gelezen')
  }

  async function markAllAsRead() {
    await alertStore.markAllAlertsAsRead()
    toastStore.success('Alle meldingen gemarkeerd als gelezen')
  }

  async function open(alert: Alert) {
    show.value = false
    await openAlert(alert)
  }

  async function goToAlertsPage() {
    show.value = false
    await router.push({ name: 'alert-overview' })
  }
</script>

<template>
  <Sheet :is-open="show" title="Meldingen" width="28rem" @close="show = false">
    <div class="toolbar">
      <div class="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="tab"
          :class="{ 'is-active': tab === 'unread' }"
          :aria-selected="tab === 'unread'"
          @click="tab = 'unread'"
        >
          Ongelezen
          <span v-if="unreadAlerts.length > 0" class="tab-count">{{ unreadAlerts.length }}</span>
        </button>
        <button
          type="button"
          role="tab"
          class="tab"
          :class="{ 'is-active': tab === 'all' }"
          :aria-selected="tab === 'all'"
          @click="tab = 'all'"
        >
          Alle
        </button>
      </div>

      <UiButton
        v-if="alertStore.unreadAlertCount > 0"
        variant="link"
        size="small"
        icon="check"
        icon-position="left"
        label="Alles gelezen"
        @click="markAllAsRead"
      />
    </div>

    <section v-for="group in alertGroups" :key="group.key" class="group">
      <h3 class="group-label">{{ group.label }}</h3>

      <ul class="alerts">
        <li
          v-for="alert in group.alerts"
          :key="alert.id"
          class="alert"
          :class="{ 'is-unread': alert.status === AlertStatus.UNREAD }"
          :tabindex="alert.status === AlertStatus.UNREAD ? 0 : undefined"
          @click="markAsRead(alert)"
          @keydown.enter="markAsRead(alert)"
        >
          <span class="unread-dot" />

          <div class="alert-content">
            <p class="alert-title">{{ alert.title }}</p>
            <p v-if="alert.description" class="alert-description">{{ alert.description }}</p>
            <span class="alert-time">{{ formatDateTime(alert.createdAt) }}</span>

            <UiButton
              v-if="alert.url"
              class="alert-action"
              label="Bekijk"
              size="small"
              @click.stop="open(alert)"
            />
          </div>
        </li>
      </ul>
    </section>

    <p v-if="alertGroups.length === 0" class="empty">{{ emptyMessage }}</p>

    <template #footer>
      <div class="footer">
        <Popup side="top">
          <template #trigger="{ open: isOpen, toggle }">
            <button type="button" class="period-trigger" :aria-expanded="isOpen" @click="toggle">
              {{ periodLabels[period] }}
              <ChevronDown class="chevron" :class="{ 'is-open': isOpen }" />
            </button>
          </template>

          <template #default="{ close }">
            <div class="period-options">
              <button
                v-for="(label, option) in periodLabels"
                :key="option"
                type="button"
                class="period-option"
                :class="{ 'is-active': option === period }"
                @click="selectPeriod(option, close)"
              >
                {{ label }}
              </button>
            </div>
          </template>
        </Popup>

        <UiButton
          variant="link"
          size="small"
          icon="arrowRight"
          icon-position="right"
          label="Alle meldingen"
          @click="goToAlertsPage"
        />
      </div>
    </template>
  </Sheet>
</template>

<style lang="css" scoped>
  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--colours-grey-3);
  }

  .tabs {
    display: flex;
    gap: var(--spacing-s);
  }

  .tab {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    padding: var(--spacing-xs) 0;
    border: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    background: none;
    color: var(--colours-grey-4);
    font: inherit;
    font-size: 0.875rem;
    cursor: pointer;
  }

  .tab.is-active {
    border-bottom-color: var(--colours-grey-5);
    color: var(--colours-grey-5);
    font-weight: 600;
  }

  .tab-count {
    min-width: 1.25rem;
    padding: 0 0.375rem;
    border-radius: 0.625rem;
    background-color: color-mix(in srgb, var(--colours-brand-colour-1) 12%, var(--colours-grey-1));
    color: var(--colours-brand-colour-1);
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.25rem;
    text-align: center;
  }

  .group-label {
    margin: var(--spacing-s) 0 var(--spacing-xs);
    color: var(--colours-grey-4);
    font-size: 0.75rem;
    font-weight: 600;
  }

  .alerts {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .alert {
    display: flex;
    align-items: start;
    gap: var(--spacing-xs);
    padding: 0.75rem var(--spacing-xs);
    border: 1px solid var(--colours-grey-3);
    border-radius: 0.75rem;
  }

  .alert.is-unread {
    border-color: color-mix(in srgb, var(--colours-brand-colour-1) 25%, var(--colours-grey-1));
    background-color: color-mix(in srgb, var(--colours-brand-colour-1) 6%, var(--colours-grey-1));
    cursor: pointer;
  }

  .alert.is-unread:hover {
    background-color: color-mix(in srgb, var(--colours-brand-colour-1) 10%, var(--colours-grey-1));
  }

  .unread-dot {
    flex-shrink: 0;
    width: 0.5rem;
    height: 0.5rem;
    margin-top: 0.375rem;
    border-radius: 50%;
  }

  .is-unread .unread-dot {
    background-color: var(--colours-brand-colour-1);
  }

  .alert-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .alert-title {
    margin: 0;
    color: var(--colours-grey-4);
    font-size: 0.875rem;
  }

  .is-unread .alert-title {
    color: var(--colours-grey-5);
    font-weight: 600;
  }

  .alert-description {
    display: -webkit-box;
    margin: 0;
    overflow: hidden;
    color: var(--colours-grey-4);
    font-size: 0.8125rem;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .alert-time {
    margin-top: 0.125rem;
    color: var(--colours-grey-4);
    font-size: 0.75rem;
  }

  .alert-action {
    align-self: start;
    margin-top: var(--spacing-xs);
  }

  .empty {
    margin: var(--spacing-l) 0;
    color: var(--colours-grey-4);
    font-size: 0.875rem;
    text-align: center;
  }

  .footer {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: var(--spacing-s);
    border-top: 1px solid var(--colours-grey-3);
  }

  .period-trigger {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    padding: 0;
    border: none;
    background: none;
    color: var(--colours-grey-4);
    font: inherit;
    font-size: 0.875rem;
    cursor: pointer;
  }

  .chevron {
    width: 1rem;
    height: 1rem;
    transition: transform 150ms ease-in-out;
  }

  .chevron.is-open {
    transform: rotate(180deg);
  }

  .period-options {
    display: flex;
    flex-direction: column;
    white-space: nowrap;
  }

  .period-option {
    padding: var(--spacing-xs) var(--spacing-s);
    border: none;
    border-radius: 0.5rem;
    background: none;
    color: var(--colours-grey-5);
    font: inherit;
    font-size: 0.875rem;
    text-align: left;
    cursor: pointer;
  }

  .period-option:hover {
    background-color: var(--colours-grey-2);
  }

  .period-option.is-active {
    color: var(--colours-brand-colour-1);
    font-weight: 600;
  }

  @media (prefers-reduced-motion: reduce) {
    .chevron {
      transition: none;
    }
  }
</style>
